import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DatePicker, Tooltip, Whisper } from 'rsuite';
import { PERIOD_STATUS, dateFromIso, isoFromDate } from '../../utils/timesheet';
import SlideSwap from './SlideSwap';

// Small pieces shared by the four timesheet pages.

// Switching between the timesheet's pages: My Timesheet, Approvals, Reports,
// and its admin settings (which live under Admin > Configuration). Only the
// pages the person can use are listed. Every page puts it last in its
// header's actions, so it sits in the same top-right spot on each.
export function TimesheetNav({ config, children = null }) {
  const location = useLocation();
  const perms = config?.permissions || {};
  const items = [
    { to: '/timesheet', label: 'My Timesheet', icon: 'bi-stopwatch' },
    perms.canApprove ? { to: '/timesheet/approvals', label: 'Approvals', icon: 'bi-check2-square' } : null,
    { to: '/timesheet/reports', label: 'Reports', icon: 'bi-bar-chart-line' },
    perms.canManage ? { to: '/admin/timesheet', label: 'Admin', icon: 'bi-gear' } : null,
  ].filter(Boolean);

  return (
    <div className="ts-nav">
      <nav className="bdc-segment" aria-label="Timesheet pages">
        {items.map((item) => {
          const active = item.to === '/timesheet'
            ? location.pathname === '/timesheet'
            : location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`bdc-btn bdc-btn--sm${active ? ' bdc-btn--primary' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <i className={`bi ${item.icon}`} aria-hidden="true" />
              {item.label}
            </Link>
          );
        })}
      </nav>
      {children}
    </div>
  );
}

export function StatusPill({ status }) {
  const meta = PERIOD_STATUS[status] || PERIOD_STATUS.open;
  return (
    <span className={`ts-pill ts-pill--${meta.tone}`}>
      <i className={`bi ${meta.icon}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

export function Banner({ tone = 'info', icon = 'bi-info-circle', title = null, children, actions = null }) {
  return (
    <div className={`ts-banner${tone !== 'info' ? ` ts-banner--${tone}` : ''}`} role={tone === 'warn' || tone === 'bad' ? 'alert' : undefined}>
      <i className={`bi ${icon}`} aria-hidden="true" />
      <div className="ts-banner__body">
        {title ? <div className="ts-banner__title">{title}</div> : null}
        {children}
      </div>
      {actions ? <div className="ts-banner__actions">{actions}</div> : null}
    </div>
  );
}

// Only says something when there is something wrong: the tables not being
// installed yet.
export function ProviderBanner({ config }) {
  if (!config || config.installed) return null;
  return (
    <Banner tone="warn" icon="bi-exclamation-triangle" title="The timesheet is not set up yet">
      The database tables for the timesheet have not been installed. An administrator needs to run
      {' '}<code>server/sql/timesheet_system.sql</code>.
    </Banner>
  );
}

/**
 * Where you are in time, the same on every timesheet page: previous / pick a
 * date / next, then what is on screen as a heading, and "Back to today" when
 * it is somewhere else.
 *
 *   date      the ISO date the calendar opens on
 *   label     the heading - "Tomorrow, 21 October", "Pay period, Oct 16 - 31"
 *   unit      what a step moves by, for the arrows' labels ("day", "pay period")
 *   onStep    (-1 | 1) when an arrow is pressed
 *   onJump    (iso) when a date is picked
 *   onToday   back to today; the link shows when `showToday` is true
 *
 * The heading slides toward the side it moved to. `slideKey` names what is
 * shown; a page whose content slides with it passes its own `slideDir` so the
 * two move together, otherwise the direction is worked out from the keys.
 */
export function DateStepper({ date, label, unit = 'day', onStep, onJump, onToday, showToday, slideKey, slideDir = null }) {
  const [jumpOpen, setJumpOpen] = useState(false);
  const last = useRef({ key: slideKey, dir: 0 });
  if (last.current.key !== slideKey) {
    last.current = { key: slideKey, dir: String(slideKey) > String(last.current.key) ? 1 : -1 };
  }
  const direction = slideDir ?? last.current.dir;

  return (
    <div className="ts-toolbar__left">
      <div className="bdc-segment">
        <button type="button" className="bdc-btn ts-toolbar__step" onClick={() => onStep(-1)} aria-label={`Previous ${unit}`} title="Previous">
          <i className="bi bi-chevron-left" aria-hidden="true" />
        </button>
        <span className="position-relative d-inline-flex">
          <button type="button" className="bdc-btn ts-toolbar__jump" onClick={() => setJumpOpen(true)} aria-label="Pick a date" title="Pick a date">
            <i className="bi bi-calendar3" aria-hidden="true" />
            <i className="bi bi-chevron-down" aria-hidden="true" style={{ fontSize: 10 }} />
          </button>
          {/* An invisible picker anchored to the button, so the calendar opens under it. */}
          <DatePicker
            open={jumpOpen}
            onClose={() => setJumpOpen(false)}
            value={dateFromIso(date)}
            onChange={(value) => {
              if (value) onJump(isoFromDate(value));
              setJumpOpen(false);
            }}
            oneTap
            isoWeek={false}
            weekStart={0}
            cleanable={false}
            placement="bottomStart"
            style={{ position: 'absolute', inset: 0, opacity: 0, pointerEvents: 'none' }}
            tabIndex={-1}
            aria-hidden="true"
          />
        </span>
        <button type="button" className="bdc-btn ts-toolbar__step" onClick={() => onStep(1)} aria-label={`Next ${unit}`} title="Next">
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
      </div>
      <SlideSwap as="h2" innerAs="span" swapKey={slideKey} direction={direction} className="ts-toolbar__label ts-swap--inline" aria-live="polite">
        {label}
      </SlideSwap>
      {showToday ? (
        <button type="button" className="bdc-linkbtn ts-toolbar__today" onClick={onToday}>Back to today</button>
      ) : null}
    </div>
  );
}

// The task picker lives in its own file; re-exported so the pages keep one
// import for the timesheet's small pieces.
export { default as TaskPicker } from './TaskPicker';

// An entry's R&D credit flag as a small chip: filled when the time counts
// toward the credit, outlined when it does not. With `onToggle` it is a button
// that flips it; without, just a label.
export function RdChip({ on, onToggle = null }) {
  const tip = onToggle
    ? (on ? 'Counts toward the R&D credit. Click to leave it out.' : 'Not counted toward the R&D credit. Click to count it.')
    : (on ? 'Counts toward the R&D credit.' : 'Not counted toward the R&D credit.');
  const className = `ts-rdchip${on ? ' is-on' : ''}`;
  return (
    <Whisper placement="top" trigger="hover" speaker={<Tooltip>{tip}</Tooltip>}>
      {onToggle ? (
        <button type="button" className={className} aria-pressed={on} aria-label={tip} onClick={onToggle}>R&amp;D</button>
      ) : (
        <span className={className} aria-label={tip}>R&amp;D</span>
      )}
    </Whisper>
  );
}

export function Switch({ checked, onChange, label, disabled = false }) {
  return (
    <label className="bdc-switch">
      <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="bdc-switch__track"><span className="bdc-switch__thumb" /></span>
      {label}
    </label>
  );
}

export function Metric({ label, value, foot = null, footTone = null }) {
  return (
    <div className="bdc-metric">
      <div className="bdc-metric__body">
        <div>
          <p className="bdc-metric__label">{label}</p>
          <p className="bdc-metric__value bdc-num">{value ?? '—'}</p>
        </div>
        {foot ? <div className={`bdc-metric__foot${footTone ? ` bdc-metric__foot--${footTone}` : ''}`}>{foot}</div> : null}
      </div>
    </div>
  );
}

// `color` is the project's, which its tasks inherit unless they set their own.
export function TaskCell({ task, color = null, fallback = 'Unknown task' }) {
  if (!task) return <span className="nsi-table__muted">{fallback}</span>;
  const own = task.pathNames.length > 1 ? task.pathNames.slice(1).join(' / ') : task.name;
  return (
    <div className="ts-taskcell" title={task.path}>
      <span className="ts-taskcell__dot" style={task.color || color ? { background: task.color || color } : undefined} aria-hidden="true" />
      <div className="ts-taskcell__text">
        <div className="ts-taskcell__name">{own}</div>
        {task.pathNames.length > 1 ? <div className="ts-taskcell__project">{task.pathNames[0]}</div> : null}
      </div>
    </div>
  );
}

// A clock time as 'HH:MM' (24-hour, what the API speaks) as a Date. Null when
// empty.
const clockToDate = (clock) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(clock || ''));
  return m ? new Date(2000, 0, 1, Number(m[1]), Number(m[2])) : null;
};

export { default as ClockPicker } from './ClockPicker';

// What a fix button says: the server's own words, when it sent them.
const fixLabel = (fix) => {
  if (!fix) return null;
  if (fix.label) return fix.label;
  if (fix.kind === 'set_duration') {
    const h = Math.floor(fix.seconds / 3600);
    const m = Math.round((fix.seconds % 3600) / 60);
    return `Round to ${h}h ${String(m).padStart(2, '0')}m`;
  }
  return null;
};

// The validation problems that belong to one place - a day, the pay period -
// as one banner each: failures in the danger color, warnings in the warning
// one. A problem the server knows how to fix gets a button when `onFix` is
// given; `fixing` is the key of the one being applied. Nothing at all when
// there are no problems.
export function ValidationBanners({ issues, onFix = null, fixing = null }) {
  if (!issues?.length) return null;
  return (
    <div className="ts-checks">
      {issues.map((issue) => {
        const label = onFix ? fixLabel(issue.fix) : null;
        return (
          <Banner
            key={issue.key}
            tone={issue.severity === 'fail' ? 'bad' : 'warn'}
            icon="bi-exclamation-triangle"
            title={issue.name}
            actions={label ? (
              <button
                type="button"
                className="bdc-btn bdc-btn--sm"
                onClick={() => onFix(issue)}
                disabled={Boolean(fixing)}
              >
                <i className={`bi ${fixing === issue.key ? 'bi-hourglass-split' : 'bi-magic'}`} aria-hidden="true" />
                {label}
              </button>
            ) : null}
          >
            {issue.detail}
          </Banner>
        );
      })}
    </div>
  );
}

// A small warning mark for an entry or a day with problems; hovering it lists
// them in an rsuite tooltip. Worst severity wins the color.
//
// The mark sits inside the entry's task button, so it stops the click (it is
// not a request to change the task) and carries an empty title, which keeps
// the button's own native tooltip from showing over this one.
export function ValidationFlag({ issues, className = '' }) {
  if (!issues?.length) return null;
  const severity = issues.some((i) => i.severity === 'fail') ? 'fail' : 'warn';
  const text = issues.map((i) => `${i.name}: ${i.detail}`).join('. ');
  return (
    <Whisper
      placement="top"
      trigger="hover"
      speaker={(
        <Tooltip className="ts-flagtip">
          {issues.map((issue) => (
            <div key={issue.key} className="ts-flagtip__item">
              <strong>{issue.name}</strong>
              <span>{issue.detail}</span>
            </div>
          ))}
        </Tooltip>
      )}
    >
      <i
        className={`bi bi-exclamation-triangle-fill ts-flag ts-flag--${severity}${className ? ` ${className}` : ''}`}
        title=""
        aria-label={text}
        role="img"
        onClick={(e) => e.stopPropagation()}
      />
    </Whisper>
  );
}

// Seconds between two 'HH:MM' times; null when either is missing or the end is
// before the start. Zero is a real answer: a meal-break marker starts and ends
// at the same minute.
export const clockSpanSeconds = (start, end) => {
  const s = clockToDate(start);
  const e = clockToDate(end);
  if (!s || !e) return null;
  const diff = (e - s) / 1000;
  return diff >= 0 ? diff : null;
};

// 'HH:MM' moved on by a number of seconds; null when that runs past midnight.
export const addToClock = (clock, seconds) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(clock || ''));
  if (!m) return null;
  const total = Number(m[1]) * 60 + Number(m[2]) + Math.round((Number(seconds) || 0) / 60);
  return total < 24 * 60 ? `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}` : null;
};

// Where the next entry on a day most likely starts: when the last one ended.
export const suggestStartTime = (entries) => entries
  .map((e) => e.endTime)
  .filter(Boolean)
  .sort()
  .pop() || null;
